import { useState, useEffect } from "react"
import PropTypes from "prop-types"
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  InputAdornment,
  IconButton,
  Checkbox,
  Chip,
  Box,
} from "@mui/material"
import SearchIcon from "@mui/icons-material/Search"
import ClearIcon from "@mui/icons-material/Clear"
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown"
import MDBox from "components/MDBox"
import MDTypography from "components/MDTypography"
import MDButton from "components/MDButton"

/**
 * ModalSelectorMultiple - Componente reutilizable para seleccionar múltiples items de una lista con búsqueda
 *
 * @param {array} value - Array de IDs seleccionados
 * @param {function} onChange - Callback cuando cambia la selección, recibe el array de IDs seleccionados
 * @param {array} items - Array de objetos con la estructura definida por itemValue e itemLabel
 * @param {string} itemValue - Nombre de la propiedad que contiene el ID del item (default: "id")
 * @param {string} itemLabel - Nombre de la propiedad que contiene la descripción del item (default: "descripcion")
 * @param {function} renderChip - Función personalizada para renderizar los chips (recibe el item completo)
 * @param {string} label - Etiqueta del campo
 * @param {string} placeholder - Texto placeholder del input
 * @param {string} searchPlaceholder - Texto placeholder del buscador en el modal
 * @param {string} emptyText - Texto cuando no hay resultados
 * @param {boolean} fullWidth - Si el input ocupa todo el ancho
 * @param {boolean} disabled - Si el componente está deshabilitado
 * @param {boolean} showClearButton - Si muestra el botón de limpiar todo
 */
const ModalSelectorMultiple = ({
  value = [],
  onChange,
  items = [],
  itemValue = "id",
  itemLabel = "descripcion",
  renderChip,
  label = "Seleccionar",
  placeholder = "Seleccione opciones",
  searchPlaceholder = "Buscar...",
  emptyText = "No se encontraron resultados",
  fullWidth = true,
  disabled = false,
  showClearButton = true,
}) => {
  const [open, setOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")
  const [filteredItems, setFilteredItems] = useState([])
  const [tempValue, setTempValue] = useState([])

  useEffect(() => {
    if (searchTerm === "") {
      setFilteredItems(items)
    } else {
      const filtered = items.filter((item) =>
        item[itemLabel].toLowerCase().includes(searchTerm.toLowerCase())
      )
      setFilteredItems(filtered)
    }
  }, [searchTerm, items, itemLabel])

  const handleOpen = () => {
    if (!disabled) {
      setTempValue([...value]) // Guardar una copia temporal
      setOpen(true)
      setSearchTerm("")
    }
  }

  const handleClose = () => {
    setOpen(false)
    setSearchTerm("")
  }

  const handleCancel = () => {
    setTempValue([...value]) // Restaurar el valor original
    handleClose()
  }

  const handleConfirm = () => {
    onChange(tempValue)
    handleClose()
  }

  const handleToggle = (itemId) => {
    const currentIndex = tempValue.indexOf(itemId)
    const newValue = [...tempValue]

    if (currentIndex === -1) {
      newValue.push(itemId)
    } else {
      newValue.splice(currentIndex, 1)
    }

    setTempValue(newValue)
  }

  const handleClearAll = (e) => {
    e.stopPropagation()
    onChange([])
  }

  const handleRemoveChip = (itemId, e) => {
    e.stopPropagation()
    const newValue = value.filter((id) => id !== itemId)
    onChange(newValue)
  }

  const handleSelectAll = () => {
    const allIds = items.map((item) => item[itemValue])
    setTempValue(allIds)
  }

  const handleDeselectAll = () => {
    setTempValue([])
  }

  const getSelectedItems = () => {
    return items.filter((item) => value.includes(item[itemValue]))
  }

  const maxVisibleChips = 2

  return (
    <>
      <TextField
        fullWidth={fullWidth}
        label={label}
        placeholder={value.length === 0 ? placeholder : ""}
        onClick={handleOpen}
        disabled={disabled}
        variant="outlined"
        value=""
        InputProps={{
          readOnly: true,
          sx: {
            cursor: disabled ? "not-allowed" : "pointer",
            minHeight: "56px",
            paddingRight: "14px !important",
            "& input": {
              cursor: disabled ? "not-allowed" : "pointer",
              display: value.length > 0 ? "none" : "block",
            },
          },
          startAdornment: value.length > 0 && (
            <Box sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 0.5,
              py: 0.5,
              pr: 1,
              maxWidth: "100%",
              alignItems: "center"
            }}>
              {getSelectedItems().slice(0, maxVisibleChips).map((item) => (
                <Chip
                  key={item[itemValue]}
                  label={
                    renderChip ? renderChip(item) : item[itemLabel]
                  }
                  onDelete={(e) => handleRemoveChip(item[itemValue], e)}
                  size="small"
                  sx={{
                    maxWidth: "200px",
                    backgroundColor: "#1976d2",
                    color: "#ffffff",
                    "& .MuiChip-label": {
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      display: "block"
                    },
                    "& .MuiChip-deleteIcon": {
                      color: "#ffffff",
                      "&:hover": {
                        color: "#e0e0e0"
                      }
                    }
                  }}
                />
              ))}
              {value.length > maxVisibleChips && (
                <Chip
                  label={`y ${value.length - maxVisibleChips} más`}
                  size="small"
                  sx={{
                    cursor: "pointer",
                    backgroundColor: "#757575",
                    color: "#ffffff"
                  }}
                />
              )}
            </Box>
          ),
          endAdornment: (
            <InputAdornment position="end">
              {showClearButton && value.length > 0 && !disabled && (
                <IconButton
                  size="small"
                  onClick={handleClearAll}
                  sx={{
                    mr: 0.5,
                    color: "#344767",
                    "&:hover": {
                      color: "#1976d2",
                      backgroundColor: "rgba(25, 118, 210, 0.08)"
                    }
                  }}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              )}
              <ArrowDropDownIcon sx={{ color: "#344767" }} />
            </InputAdornment>
          ),
        }}
        InputLabelProps={{
          shrink: true,
        }}
      />

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            minHeight: "400px",
            maxHeight: "600px",
          },
        }}
      >
        <DialogTitle>
          <MDBox display="flex" justifyContent="space-between" alignItems="center">
            <MDTypography variant="h5" fontWeight="medium" sx={{ color: "text.primary" }}>
              Seleccionar {label}
            </MDTypography>
            <MDBox display="flex" alignItems="center" gap={2}>
              <MDTypography variant="caption" sx={{ color: "text.secondary" }}>
                {tempValue.length} seleccionado{tempValue.length !== 1 ? "s" : ""}
              </MDTypography>
              {tempValue.length === items.length ? (
                <MDButton
                  variant="text"
                  color="secondary"
                  size="small"
                  onClick={handleDeselectAll}
                  sx={{ textTransform: "none", minWidth: "auto" }}
                >
                  Deseleccionar todo
                </MDButton>
              ) : (
                <MDButton
                  variant="text"
                  color="info"
                  size="small"
                  onClick={handleSelectAll}
                  sx={{ textTransform: "none", minWidth: "auto" }}
                >
                  Seleccionar todo
                </MDButton>
              )}
            </MDBox>
          </MDBox>
        </DialogTitle>
        <DialogContent>
          <MDBox sx={{ mb: 2 }}>
            <TextField
              fullWidth
              placeholder={searchPlaceholder}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              variant="outlined"
              autoFocus
              InputProps={{
                sx: {
                  color: "#344767",
                  "& input": {
                    color: "#344767",
                  },
                },
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "#344767" }} />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setSearchTerm("")}
                      sx={{ color: "#344767" }}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </MDBox>

          {filteredItems.length > 0 ? (
            <List sx={{ pt: 0 }}>
              {filteredItems.map((item) => {
                const isChecked = tempValue.includes(item[itemValue])
                return (
                  <ListItem key={item[itemValue]} disablePadding>
                    <ListItemButton
                      onClick={() => handleToggle(item[itemValue])}
                      selected={isChecked}
                      dense
                    >
                      <Checkbox
                        edge="start"
                        checked={isChecked}
                        tabIndex={-1}
                        disableRipple
                        sx={{
                          color: "#7b809a !important",
                          "& .MuiSvgIcon-root": {
                            color: "#7b809a !important",
                            border: "1px solid #d2d6da !important",
                          },
                          "&.Mui-checked": {
                            color: "#1976d2 !important",
                            "& .MuiSvgIcon-root": {
                              color: "#1976d2 !important",
                              borderColor: "#1976d2 !important",
                            },
                          },
                        }}
                      />
                      <ListItemText
                        primary={renderChip ? renderChip(item) : item[itemLabel]}
                        primaryTypographyProps={{
                          variant: "body2",
                          sx: { color: "#344767" },
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                )
              })}
            </List>
          ) : (
            <MDBox
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "200px",
              }}
            >
              <MDTypography variant="body2" color="text" fontWeight="light">
                {emptyText}
              </MDTypography>
            </MDBox>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <MDButton onClick={handleCancel} variant="outlined" color="secondary">
            Cancelar
          </MDButton>
          <MDButton onClick={handleConfirm} variant="gradient" color="info">
            Confirmar
          </MDButton>
        </DialogActions>
      </Dialog>
    </>
  )
}

ModalSelectorMultiple.propTypes = {
  value: PropTypes.array,
  onChange: PropTypes.func.isRequired,
  items: PropTypes.array,
  itemValue: PropTypes.string,
  itemLabel: PropTypes.string,
  renderChip: PropTypes.func,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  searchPlaceholder: PropTypes.string,
  emptyText: PropTypes.string,
  fullWidth: PropTypes.bool,
  disabled: PropTypes.bool,
  showClearButton: PropTypes.bool,
}

export default ModalSelectorMultiple
